import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import type { ChipProps } from '@mui/material/Chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddLinkIcon from '@mui/icons-material/AddLink';
import TimelineIcon from '@mui/icons-material/Timeline';
import EditIcon from '@mui/icons-material/Edit';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  CoralResponse,
  CoralLogEntry,
  CreateCoralLogPayload,
  UpdateCoralLogPayload,
  CoralFormData,
} from '../types';
import {
  getCoral,
  getCoralLogs,
  createCoralLog,
  updateCoralLog,
  deleteCoralLog,
  updateCoral,
} from '../services/api';
import CoralFamily from '../components/CoralFamily';
import { brand } from '../theme';

const MAX_INITIAL_LOGS = 25;
const LOGS_PER_PAGE = 20;
const LOG_TABLE_MAX_HEIGHT = 600;

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const padToTwo = (value: number) => value.toString().padStart(2, '0');

const toInputDateTimeValue = (date: Date): string => {
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = padToTwo(date.getMonth() + 1);
  const day = padToTwo(date.getDate());
  const hours = padToTwo(date.getHours());
  const minutes = padToTwo(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDateTimeInputValue = (iso?: string): string => {
  if (!iso) {
    return '';
  }
  return toInputDateTimeValue(new Date(iso));
};

const getDefaultEntryDateInputValue = (): string => toInputDateTimeValue(new Date());

const toIsoStringFromInputValue = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

const LOG_STATUS_COLOR_MAP: Record<string, ChipProps['color']> = {
  ALIVE: 'success',
  EXPORTED: 'info',
  DECEASED: 'error',
};

const LOG_TEXT_CLAMP_SX = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
};

const getLogStatusColor = (status?: string): ChipProps['color'] => {
  if (!status) {
    return 'default';
  }
  return LOG_STATUS_COLOR_MAP[status.toUpperCase()] ?? 'default';
};

const formatLogStatusLabel = (status: string) =>
  status
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const LOG_TYPE_OPTIONS = ['MEASURE'] as const;
const LOG_STATUS_OPTIONS = ['ALIVE', 'EXPORTED', 'DECEASED'] as const;

const isLogTypeOption = (value?: string): value is (typeof LOG_TYPE_OPTIONS)[number] =>
  value ? (LOG_TYPE_OPTIONS as readonly string[]).includes(value) : false;

const isLogStatusOption = (value?: string): value is (typeof LOG_STATUS_OPTIONS)[number] =>
  value ? (LOG_STATUS_OPTIONS as readonly string[]).includes(value) : false;

type CreateLogFormState = {
  logType: (typeof LOG_TYPE_OPTIONS)[number];
  status: (typeof LOG_STATUS_OPTIONS)[number];
  entryDate: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  diameterCm: string;
  weightG: string;
  notes: string;
};

const createEmptyLogForm = (currentStatus?: string): CreateLogFormState => {
  return {
    logType: 'MEASURE',
    status: (currentStatus as (typeof LOG_STATUS_OPTIONS)[number]) || 'ALIVE',
    entryDate: getDefaultEntryDateInputValue(),
    lengthCm: '',
    widthCm: '',
    heightCm: '',
    diameterCm: '',
    weightG: '',
    notes: '',
  };
};

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const trimmed = value.toString().trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseDateTimeTimestamp = (value?: string): number | null => {
  if (!value) {
    return null;
  }
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? null : ts;
};

const getLogSortKey = (log: CoralLogEntry): [number, number, number] => {
  const entryTs = parseDateTimeTimestamp(log.entryDate ?? log.createdAt) ?? 0;
  const updatedTs = parseDateTimeTimestamp(log.updatedAt);
  const createdTs = parseDateTimeTimestamp(log.createdAt) ?? 0;
  const effectiveUpdatedTs = updatedTs ?? createdTs;
  return [entryTs, effectiveUpdatedTs, createdTs];
};

const getLatestLogEntry = (entries: CoralLogEntry[]): CoralLogEntry | null => {
  if (!entries.length) {
    return null;
  }

  return entries.slice(1).reduce<CoralLogEntry>((latest, candidate) => {
    const latestKey = getLogSortKey(latest);
    const candidateKey = getLogSortKey(candidate);

    for (let i = 0; i < latestKey.length; i += 1) {
      if (candidateKey[i] === latestKey[i]) {
        continue;
      }
      return candidateKey[i] > latestKey[i] ? candidate : latest;
    }
    return latest;
  }, entries[0]);
};

const CoralDetails: React.FC = () => {
  const { coralId } = useParams<{ coralId: string }>();
  const [coral, setCoral] = useState<CoralResponse | null>(null);
  const [logs, setLogs] = useState<CoralLogEntry[]>([]);
  const [logsCursor, setLogsCursor] = useState<string | undefined>();
  const [logsPage, setLogsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [refreshingLogs, setRefreshingLogs] = useState(false);
  const [isCreateLogOpen, setIsCreateLogOpen] = useState(false);
  const [createLogValues, setCreateLogValues] = useState<CreateLogFormState>(() =>
    createEmptyLogForm(undefined)
  );
  const [selectedLog, setSelectedLog] = useState<CoralLogEntry | null>(null);
  const [createLogError, setCreateLogError] = useState<string | null>(null);
  const [savingLog, setSavingLog] = useState(false);
  const [deletingLog, setDeletingLog] = useState(false);
  const [isEditCoralOpen, setIsEditCoralOpen] = useState(false);
  const [editCoralValues, setEditCoralValues] = useState<CoralFormData>(() => ({
    speciesCode: '',
    cutDate: '',
    condition: 'ALIVE',
  }));
  const [editingCoral, setEditingCoral] = useState(false);
  const [editCoralError, setEditCoralError] = useState<string | null>(null);
  const [isAttachRfidOpen, setIsAttachRfidOpen] = useState(false);
  const [attachRfidValue, setAttachRfidValue] = useState('');
  const [attachRfidError, setAttachRfidError] = useState<string | null>(null);
  const [attachingRfid, setAttachingRfid] = useState(false);
  const isEditMode = Boolean(selectedLog);
  const latestLog = useMemo(() => getLatestLogEntry(logs), [logs]);
  const latestStatus = latestLog?.status || coral?.status || null;
  const latestStatusColor = getLogStatusColor(latestStatus || undefined);
  const latestUpdatedTimestamp = latestLog?.updatedAt || latestLog?.createdAt || coral?.updatedAt;

  const fetchLogs = useCallback(
    async ({ cursor, append = false }: { cursor?: string; append?: boolean } = {}) => {
      if (!coralId) {
        return 0;
      }

      if (append && !cursor) {
        return 0;
      }

      let fetchedCount = 0;

      try {
        if (append) {
          setLoadingMoreLogs(true);
        } else {
          setRefreshingLogs(true);
        }

        const response = await getCoralLogs(coralId, MAX_INITIAL_LOGS, cursor);
        const nextItems = response.items || [];
        fetchedCount = nextItems.length;
        setLogs((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setLogsCursor(response.nextCursor);
        if (!append) {
          setLogsPage(1);
        }
        setLogsError(null);
      } catch (err: any) {
        const message = err?.message || 'Failed to load tracking logs';
        setLogsError(message);

        if (!append) {
          setLogs([]);
          setLogsCursor(undefined);
        }
      } finally {
        if (append) {
          setLoadingMoreLogs(false);
        } else {
          setRefreshingLogs(false);
        }
      }

      return fetchedCount;
    },
    [coralId]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!coralId) {
        setError('Coral ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const coralResponse = await getCoral(coralId);
        setCoral(coralResponse);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load coral details');
      } finally {
        setLoading(false);
      }

      void fetchLogs();
    };

    fetchData();
  }, [coralId, fetchLogs]);
  const handleRefreshLogs = useCallback(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handlePrevLogsPage = useCallback(() => {
    if (refreshingLogs || loadingMoreLogs) {
      return;
    }
    setLogsPage((prev) => Math.max(1, prev - 1));
  }, [loadingMoreLogs, refreshingLogs]);

  const handleNextLogsPage = useCallback(async () => {
    if (loadingMoreLogs || refreshingLogs) {
      return;
    }

    const totalLogPages = Math.max(1, Math.ceil(logs.length / LOGS_PER_PAGE));
    if (logsPage < totalLogPages) {
      setLogsPage((prev) => prev + 1);
      return;
    }

    if (!logsCursor) {
      return;
    }

    const newCount = await fetchLogs({ cursor: logsCursor, append: true });
    if (newCount > 0) {
      setLogsPage((prev) => prev + 1);
    }
  }, [fetchLogs, loadingMoreLogs, logs.length, logsCursor, logsPage, refreshingLogs]);

  const handleOpenCreateLog = useCallback(() => {
    setSelectedLog(null);
    setCreateLogValues(createEmptyLogForm(coral?.status));
    setCreateLogError(null);
    setIsCreateLogOpen(true);
  }, [coral]);

  const handleCloseCreateLog = useCallback(() => {
    if (savingLog || deletingLog) {
      return;
    }
    setIsCreateLogOpen(false);
    setCreateLogError(null);
    setCreateLogValues(createEmptyLogForm(coral?.status));
    setSelectedLog(null);
  }, [coral, deletingLog, savingLog]);

  const handleCreateLogFieldChange = useCallback(
    (field: keyof CreateLogFormState) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setCreateLogValues((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleEditLog = useCallback((log: CoralLogEntry) => {
    const nextLogType: CreateLogFormState['logType'] = isLogTypeOption(log.logType)
      ? log.logType
      : 'MEASURE';
    const nextStatus: CreateLogFormState['status'] = isLogStatusOption(log.status)
      ? (log.status as CreateLogFormState['status'])
      : 'ALIVE';
    const entryDateValue =
      formatDateTimeInputValue(log.entryDate || log.createdAt) || getDefaultEntryDateInputValue();

    setSelectedLog(log);
    setCreateLogValues({
      logType: nextLogType,
      status: nextStatus,
      entryDate: entryDateValue,
      lengthCm: typeof log.lengthCm === 'number' ? log.lengthCm.toString() : '',
      widthCm: typeof log.widthCm === 'number' ? log.widthCm.toString() : '',
      heightCm: typeof log.heightCm === 'number' ? log.heightCm.toString() : '',
      diameterCm: typeof log.diameterCm === 'number' ? log.diameterCm.toString() : '',
      weightG: typeof log.weightG === 'number' ? log.weightG.toString() : '',
      notes: log.notes || '',
    });
    setCreateLogError(null);
    setIsCreateLogOpen(true);
  }, []);

  const handleSubmitLog = useCallback(async () => {
    if (!coralId) {
      setCreateLogError('Coral ID is required');
      return;
    }

    const trimmedNotes = createLogValues.notes.trim();
    const lengthValue = toNumberOrUndefined(createLogValues.lengthCm);
    const widthValue = toNumberOrUndefined(createLogValues.widthCm);
    const heightValue = toNumberOrUndefined(createLogValues.heightCm);
    const diameterValue = toNumberOrUndefined(createLogValues.diameterCm);
    const weightValue = toNumberOrUndefined(createLogValues.weightG);
    const entryDateIso = toIsoStringFromInputValue(createLogValues.entryDate);

    // Prevent future dates
    if (entryDateIso) {
      const entryDate = new Date(entryDateIso);
      const now = new Date();
      if (entryDate > now) {
        setCreateLogError(
          'Entry time cannot be in the future. Please select a past or current time.'
        );
        return;
      }
    }

    try {
      setSavingLog(true);
      setCreateLogError(null);

      if (isEditMode && selectedLog) {
        const payload: UpdateCoralLogPayload = {
          log_type: createLogValues.logType,
          status: createLogValues.status,
        };
        if (trimmedNotes) payload.notes = trimmedNotes;
        if (lengthValue !== undefined) payload.length_cm = lengthValue;
        if (widthValue !== undefined) payload.width_cm = widthValue;
        if (heightValue !== undefined) payload.height_cm = heightValue;
        if (diameterValue !== undefined) payload.diameter_cm = diameterValue;
        if (weightValue !== undefined) payload.weight_g = weightValue;
        if (entryDateIso) payload.entry_date = entryDateIso;

        await updateCoralLog(coralId, selectedLog.logId, payload);
      } else {
        const payload: CreateCoralLogPayload = {
          logType: createLogValues.logType,
          status: createLogValues.status,
        };
        if (trimmedNotes) payload.notes = trimmedNotes;
        if (lengthValue !== undefined) payload.lengthCm = lengthValue;
        if (widthValue !== undefined) payload.widthCm = widthValue;
        if (heightValue !== undefined) payload.heightCm = heightValue;
        if (diameterValue !== undefined) payload.diameterCm = diameterValue;
        if (weightValue !== undefined) payload.weightG = weightValue;
        if (entryDateIso) payload.entryDate = entryDateIso;

        await createCoralLog(coralId, payload);
      }

      setCreateLogValues(createEmptyLogForm(coral?.status));
      setSelectedLog(null);
      setIsCreateLogOpen(false);
      await fetchLogs();
    } catch (err: any) {
      setCreateLogError(
        err?.message ||
          (selectedLog ? 'Failed to update tracking log' : 'Failed to create tracking log')
      );
    } finally {
      setSavingLog(false);
    }
  }, [coral, coralId, createLogValues, fetchLogs, isEditMode, selectedLog]);

  const handleDeleteLog = useCallback(async () => {
    if (!coralId || !selectedLog) {
      setCreateLogError('Coral and log IDs are required');
      return;
    }

    const confirmed = window.confirm('Delete this tracking log? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      setDeletingLog(true);
      setCreateLogError(null);
      await deleteCoralLog(coralId, selectedLog.logId);
      setCreateLogValues(createEmptyLogForm(coral?.status));
      setSelectedLog(null);
      setIsCreateLogOpen(false);
      await fetchLogs();
    } catch (err: any) {
      setCreateLogError(err?.message || 'Failed to delete tracking log');
    } finally {
      setDeletingLog(false);
    }
  }, [coral, coralId, fetchLogs, selectedLog]);

  const handleOpenEditCoral = useCallback(() => {
    if (!coral) {
      return;
    }
    const nextCondition =
      (latestStatus as CoralFormData['condition']) ||
      (coral.status as CoralFormData['condition']) ||
      'ALIVE';
    setEditCoralValues({
      speciesCode: coral.speciesCode || '',
      cutDate: coral.cutDate || '',
      condition: nextCondition,
    });
    setEditCoralError(null);
    setIsEditCoralOpen(true);
  }, [coral, latestStatus]);

  const handleCloseEditCoral = useCallback(() => {
    if (editingCoral) {
      return;
    }
    setIsEditCoralOpen(false);
    setEditCoralError(null);
  }, [editingCoral]);

  const handleEditCoralFieldChange = useCallback(
    (field: keyof CoralFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setEditCoralValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSaveCoral = useCallback(async () => {
    if (!coral) {
      return;
    }

    try {
      setEditingCoral(true);
      setEditCoralError(null);
      const payload = {
        speciesCode: editCoralValues.speciesCode,
        cutDate: editCoralValues.cutDate || undefined,
        status: editCoralValues.condition,
      };
      await updateCoral(coral.coralId, payload);
      const refreshedCoral = await getCoral(coral.coralId);
      setCoral(refreshedCoral);
      setIsEditCoralOpen(false);
      await fetchLogs();
    } catch (err: any) {
      setEditCoralError(err?.message || 'Failed to update coral fragment');
    } finally {
      setEditingCoral(false);
    }
  }, [coral, editCoralValues, fetchLogs]);

  const handleOpenAttachRfid = useCallback(() => {
    setAttachRfidValue('');
    setAttachRfidError(null);
    setIsAttachRfidOpen(true);
  }, []);

  const handleCloseAttachRfid = useCallback(() => {
    if (attachingRfid) {
      return;
    }
    setIsAttachRfidOpen(false);
    setAttachRfidError(null);
  }, [attachingRfid]);

  const handleAttachRfid = useCallback(async () => {
    if (!coral) {
      return;
    }

    const rfid = attachRfidValue.trim().toUpperCase();
    if (!/^[A-Z0-9]{24}$/.test(rfid)) {
      setAttachRfidError('RFID must be exactly 24 alphanumeric characters');
      return;
    }

    try {
      setAttachingRfid(true);
      setAttachRfidError(null);
      const updatedCoral = await updateCoral(coral.coralId, { rfid });
      if (!updatedCoral) {
        throw new Error('Invalid update response');
      }
      setCoral(updatedCoral);
      setIsAttachRfidOpen(false);
      setAttachRfidValue('');
    } catch (err: any) {
      setAttachRfidError(err?.message || 'Failed to attach RFID');
    } finally {
      setAttachingRfid(false);
    }
  }, [attachRfidValue, coral]);

  const measurementSeries = useMemo(() => {
    const numericLogs = logs
      .filter((log) =>
        [log.lengthCm, log.widthCm, log.heightCm, log.diameterCm, log.weightG].some(
          (value) => typeof value === 'number'
        )
      )
      .map((log) => {
        const value = log.lengthCm ?? log.widthCm ?? log.heightCm ?? log.diameterCm ?? log.weightG;
        const entryTime = log.entryDate || log.createdAt;
        return value !== undefined
          ? {
              timestamp: new Date(entryTime).getTime(),
              label: formatDate(entryTime),
              value: Number(value),
            }
          : null;
      })
      .filter((item): item is { timestamp: number; label: string; value: number } => !!item)
      .sort((a, b) => a.timestamp - b.timestamp);

    return numericLogs;
  }, [logs]);

  const growthChartPoints = useMemo(() => {
    if (measurementSeries.length === 0) {
      return null;
    }

    const values = measurementSeries.map((point) => point.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;
    const width = 320;
    const height = 180;

    const points = measurementSeries.map((point, index) => {
      const x =
        measurementSeries.length === 1
          ? width / 2
          : (index / (measurementSeries.length - 1)) * (width - 20) + 10;
      const normalized = (point.value - minValue) / range;
      const y = height - normalized * (height - 20) - 10;
      return `${x},${y}`;
    });

    return {
      points: points.join(' '),
      width,
      height,
      minValue,
      maxValue,
      startValue: measurementSeries[0].value,
      latestValue: measurementSeries[measurementSeries.length - 1].value,
    };
  }, [measurementSeries]);

  const maxLogsPage = Math.max(1, Math.ceil(logs.length / LOGS_PER_PAGE));
  const currentLogsPage = Math.min(logsPage, maxLogsPage);
  const paginatedLogs = useMemo(() => {
    const start = (currentLogsPage - 1) * LOGS_PER_PAGE;
    return logs.slice(start, start + LOGS_PER_PAGE);
  }, [currentLogsPage, logs]);
  const hasLogs = logs.length > 0;
  const logsRangeStart = hasLogs ? (currentLogsPage - 1) * LOGS_PER_PAGE + 1 : 0;
  const logsRangeEnd = hasLogs ? logsRangeStart + paginatedLogs.length - 1 : 0;
  const canGoToNextLogsPage = currentLogsPage < maxLogsPage || Boolean(logsCursor);

  useEffect(() => {
    setLogsPage((prev) => {
      const maxPage = Math.max(1, Math.ceil(logs.length / LOGS_PER_PAGE));
      return prev > maxPage ? maxPage : prev;
    });
  }, [logs.length]);

  const isInitialLogsLoading = refreshingLogs && logs.length === 0;
  const isFetchingLogs = refreshingLogs || loadingMoreLogs;

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !coral) {
    return (
      <Box sx={{ p: 4 }}>
        <Button
          component={RouterLink}
          to="/coral/view"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to Corals
        </Button>
        <Alert severity="error">{error || 'Coral not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button component={RouterLink} to="/coral/view" startIcon={<ArrowBackIcon />}>
          View All Corals
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Coral ID:
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {coral.coralId}
          </Typography>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* TODO: commented out image section for now
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Coral Image
            </Typography>
            <Box
              sx={{
                mt: 1,
                border: '2px dashed',
                borderColor: 'grey.400',
                borderRadius: 2,
                height: 260,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Image placeholder.
                <br />
                Coral imagery from S3 will appear here.
              </Typography>
            </Box>
          </Paper>
        </Grid> */}

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" gutterBottom>
                Fragment Details
              </Typography>
              <Tooltip title="Edit coral fragment">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleOpenEditCoral}
                    aria-label="Edit coral fragment"
                    disabled={editingCoral}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  RFID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {coral.rfid || 'Untagged'}
                </Typography>
                {!coral.rfid && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddLinkIcon />}
                    onClick={handleOpenAttachRfid}
                    sx={{ mt: 1 }}
                  >
                    Attach RFID
                  </Button>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Species Code
                </Typography>
                <Typography variant="body1">{coral.speciesCode}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  {latestStatus ? (
                    <Chip
                      label={formatLogStatusLabel(latestStatus)}
                      color={latestStatusColor}
                      variant={latestStatusColor === 'default' ? 'outlined' : 'filled'}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Stack>
              </Box>
              <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ width: '100%' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cut Date
                  </Typography>
                  <Typography variant="body1">
                    {coral.cutDate ? formatDate(coral.cutDate) : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">{formatDateTime(coral.createdAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Updated
                  </Typography>
                  <Typography variant="body1">{formatDateTime(latestUpdatedTimestamp)}</Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimelineIcon fontSize="small" color="primary" />
              <Typography variant="h6">Growth Metrics</Typography>
            </Box>
            {growthChartPoints ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Showing most recent measurements across available dimensions.
                </Typography>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <svg
                    viewBox={`0 0 ${growthChartPoints.width} ${growthChartPoints.height}`}
                    width="100%"
                    height={growthChartPoints.height}
                  >
                    <defs>
                      <linearGradient id="growthGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={brand.navy} stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#4CAF50" stopOpacity="0.4" />
                      </linearGradient>
                    </defs>
                    <polyline
                      fill="none"
                      stroke="url(#growthGradient)"
                      strokeWidth={3}
                      strokeLinecap="round"
                      points={growthChartPoints.points}
                    />
                  </svg>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      First measurement
                    </Typography>
                    <Typography variant="body1">
                      {growthChartPoints.startValue.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Latest measurement
                    </Typography>
                    <Typography variant="body1">
                      {growthChartPoints.latestValue.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Range
                    </Typography>
                    <Typography variant="body1">
                      {(growthChartPoints.maxValue - growthChartPoints.minValue).toFixed(1)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Measurement logs have not been recorded yet. Growth trends will appear once
                  length, width, height, or weight data is logged.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {Boolean(coral.rfidUnattachmentHistory?.length) && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6">RFID Unattachment History</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Previous tag associations retained for audit and compliance.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>RFID</TableCell>
                  <TableCell>Unattached</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Operator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...(coral.rfidUnattachmentHistory || [])].reverse().map((entry) => (
                  <TableRow key={entry.eventId}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.rfid}</TableCell>
                    <TableCell>{formatDateTime(entry.unattachedAt)}</TableCell>
                    <TableCell>{entry.reason}</TableCell>
                    <TableCell>{entry.unattachedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <CoralFamily coral={coral} />

      <Paper
        sx={{
          mt: 4,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: 'calc(100vh - 120px)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6">Recent Activity</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a tracking log to view/edit details, or delete.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              width: '100%',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              mt: { xs: 2, sm: 0 },
            }}
          >
            <Button variant="contained" onClick={handleOpenCreateLog}>
              Add Log
            </Button>
            <Button variant="outlined" onClick={handleRefreshLogs} disabled={refreshingLogs}>
              {refreshingLogs ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>
        {isFetchingLogs && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2,
            }}
            aria-label="Loading recent activity logs"
          >
            <CircularProgress size={20} thickness={5} />
            <Typography variant="body2" color="text.secondary">
              {loadingMoreLogs ? 'Loading more activity...' : 'Refreshing activity...'}
            </Typography>
          </Box>
        )}
        {logsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {logsError}
          </Alert>
        )}
        {isInitialLogsLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading recent activity...
            </Typography>
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tracking logs have been recorded for this coral fragment yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: LOG_TABLE_MAX_HEIGHT, flexGrow: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 140, px: 1 }}>Entry Time</TableCell>
                  <TableCell>Log Type</TableCell>
                  <TableCell>Recorded By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                    Length (cm)
                  </TableCell>
                  <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                    Width (cm)
                  </TableCell>
                  <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                    Height (cm)
                  </TableCell>
                  <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                    Diameter (cm)
                  </TableCell>
                  <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                    Weight (g)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLogs.map((log) => {
                  const logStatusColor = getLogStatusColor(log.status);
                  return (
                    <TableRow
                      key={log.logId}
                      hover
                      onClick={() => handleEditLog(log)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ width: 150, px: 1 }}>
                        {formatDateTime(log.entryDate)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={LOG_TEXT_CLAMP_SX}>
                          {log.logType || 'MEASURE'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={LOG_TEXT_CLAMP_SX}>
                          {log.createdBy || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {log.status ? (
                          <Chip
                            size="small"
                            label={formatLogStatusLabel(log.status)}
                            color={logStatusColor}
                            variant={logStatusColor === 'default' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={LOG_TEXT_CLAMP_SX}>
                          {log.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                        {typeof log.lengthCm === 'number' ? log.lengthCm.toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                        {typeof log.widthCm === 'number' ? log.widthCm.toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                        {typeof log.heightCm === 'number' ? log.heightCm.toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                        {typeof log.diameterCm === 'number' ? log.diameterCm.toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 56, px: 0.5 }}>
                        {typeof log.weightG === 'number' ? log.weightG.toFixed(1) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {logs.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevLogsPage}
              disabled={currentLogsPage === 1 || refreshingLogs || loadingMoreLogs}
              size="small"
            >
              Previous Page
            </Button>
            <Typography variant="body2" color="text.secondary">
              {`Showing ${logsRangeStart}-${logsRangeEnd} (Page ${currentLogsPage})`}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNextLogsPage}
              disabled={!canGoToNextLogsPage || refreshingLogs || loadingMoreLogs}
              size="small"
            >
              Next Page
            </Button>
          </Box>
        )}
      </Paper>
      <Dialog open={isAttachRfidOpen} onClose={handleCloseAttachRfid} fullWidth maxWidth="sm">
        <DialogTitle>Attach RFID</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Scan or enter a new tag for coral ID {coral.coralId}. The RFID will be attached to
              this same coral record without changing its status, lineage, or history.
            </Typography>
            <TextField
              autoFocus
              label="New RFID"
              value={attachRfidValue}
              onChange={(event) => setAttachRfidValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleAttachRfid();
                }
              }}
              inputProps={{ maxLength: 24 }}
              helperText="Exactly 24 alphanumeric characters"
              disabled={attachingRfid}
              error={Boolean(attachRfidError)}
              fullWidth
            />
            {attachRfidError && <Alert severity="error">{attachRfidError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachRfid} disabled={attachingRfid}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleAttachRfid()}
            variant="contained"
            disabled={attachingRfid || !attachRfidValue.trim()}
            startIcon={attachingRfid ? <CircularProgress size={16} /> : <AddLinkIcon />}
          >
            {attachingRfid ? 'Attaching...' : 'Attach RFID'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isEditCoralOpen} onClose={handleCloseEditCoral} fullWidth maxWidth="sm">
        <DialogTitle>Edit Coral Fragment</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Editing coral ID {coral.coralId}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Species Code"
              value={editCoralValues.speciesCode}
              onChange={handleEditCoralFieldChange('speciesCode')}
              disabled={editingCoral}
              fullWidth
            />
            <TextField
              label="Cut Date"
              placeholder="YYYY-MM (e.g., 2025-11)"
              value={editCoralValues.cutDate}
              onChange={handleEditCoralFieldChange('cutDate')}
              disabled={editingCoral}
              fullWidth
              helperText="Optional: defaults to current month if left blank."
            />
            <TextField
              select
              label="Status"
              value={editCoralValues.condition}
              onChange={handleEditCoralFieldChange('condition')}
              disabled={editingCoral}
              fullWidth
            >
              <MenuItem value="ALIVE">Alive</MenuItem>
              <MenuItem value="DECEASED">Deceased</MenuItem>
              <MenuItem value="EXPORTED">Exported</MenuItem>
            </TextField>
            {editCoralError && <Alert severity="error">{editCoralError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditCoral} disabled={editingCoral}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCoral}
            variant="contained"
            disabled={editingCoral}
            startIcon={editingCoral ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {editingCoral ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isCreateLogOpen} onClose={handleCloseCreateLog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? 'Edit Tracking Log' : 'Add Tracking Log'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isEditMode && selectedLog && (
              <Typography variant="body2" color="text.secondary">
                Editing entry from {formatDateTime(selectedLog.entryDate || selectedLog.createdAt)}
              </Typography>
            )}
            <TextField
              label="Entry Time"
              type="datetime-local"
              value={createLogValues.entryDate}
              onChange={handleCreateLogFieldChange('entryDate')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Set the timestamp for this log entry. Leave blank to use capture time. Future dates are not allowed."
              slotProps={{
                htmlInput: {
                  max: toInputDateTimeValue(new Date()),
                },
                input: {
                  endAdornment: createLogValues.entryDate && (
                    <IconButton
                      size="small"
                      onClick={() => setCreateLogValues((prev) => ({ ...prev, entryDate: '' }))}
                      edge="end"
                    >
                      ×
                    </IconButton>
                  ),
                },
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Log Type"
                value={createLogValues.logType}
                onChange={handleCreateLogFieldChange('logType')}
                size="small"
              >
                {LOG_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {formatLogStatusLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Status"
                value={createLogValues.status}
                onChange={handleCreateLogFieldChange('status')}
                size="small"
                required
              >
                {LOG_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {formatLogStatusLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Length (cm)"
                  type="number"
                  value={createLogValues.lengthCm}
                  onChange={handleCreateLogFieldChange('lengthCm')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Width (cm)"
                  type="number"
                  value={createLogValues.widthCm}
                  onChange={handleCreateLogFieldChange('widthCm')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Height (cm)"
                  type="number"
                  value={createLogValues.heightCm}
                  onChange={handleCreateLogFieldChange('heightCm')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Diameter (cm)"
                  type="number"
                  value={createLogValues.diameterCm}
                  onChange={handleCreateLogFieldChange('diameterCm')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Weight (g)"
                  type="number"
                  value={createLogValues.weightG}
                  onChange={handleCreateLogFieldChange('weightG')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Notes"
              multiline
              minRows={3}
              value={createLogValues.notes}
              onChange={handleCreateLogFieldChange('notes')}
              fullWidth
            />
            {createLogError && <Alert severity="error">{createLogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {isEditMode && (
            <Button onClick={handleDeleteLog} color="error" disabled={savingLog || deletingLog}>
              {deletingLog ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <Button onClick={handleCloseCreateLog} disabled={savingLog || deletingLog}>
            Cancel
          </Button>
          <Button onClick={handleSubmitLog} variant="contained" disabled={savingLog || deletingLog}>
            {savingLog ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Log'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoralDetails;
